import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvDelete } from '../api/dataverseClient';
import { Task } from '../types';

// Recurring tasks — UI + generation layer over the pre-existing schema:
// dagrun_pm_recurrencepattern (pattern/interval/end condition, parent task
// lookup dagrun_pm_parenttaskidpat) and the task-side instance markers
// dagrun_pm_isrecurringinstance / dagrun_pm_parentrecurrenceid /
// dagrun_pm_occurrencenumber. Instances are generated client-side at
// pattern creation: the end condition is always bounded in the UI
// (After N occurrences or By date), so the full set is known up front.

export const RecurrencePatternType = {
  Daily:          100000000,
  Weekly:         100000001,
  BiWeekly:       100000002,
  Monthly:        100000003,
  Quarterly:      100000004,
  CustomInterval: 100000005,
  EndOfSprint:    100000006,
} as const;

export const RecurrenceIntervalUnit = {
  Days:   100000000,
  Weeks:  100000001,
  Months: 100000002,
} as const;

export const RecurrenceEndType = {
  Never:             100000000,
  AfterNOccurrences: 100000001,
  OnDate:            100000002,
} as const;

export interface RecurrencePattern {
  dagrun_pm_recurrencepatternid: string;
  dagrun_pm_name: string;
  dagrun_pm_patterntype: number;
  dagrun_pm_interval?: number | null;
  dagrun_pm_intervalunit?: number | null;
  dagrun_pm_endtype: number;
  dagrun_pm_endcount?: number | null;
  dagrun_pm_enddate?: string | null;
  dagrun_pm_lastgenerateddate?: string | null;
  _dagrun_pm_parenttaskidpat_value?: string | null;
}

const PATTERN_FIELDS = [
  'dagrun_pm_recurrencepatternid', 'dagrun_pm_name', 'dagrun_pm_patterntype',
  'dagrun_pm_interval', 'dagrun_pm_intervalunit', 'dagrun_pm_endtype',
  'dagrun_pm_endcount', 'dagrun_pm_enddate', 'dagrun_pm_lastgenerateddate',
  '_dagrun_pm_parenttaskidpat_value',
];

/** The recurrence pattern owned by a task, if any (one per task). */
export async function getTaskRecurrencePattern(
  pca: IPublicClientApplication,
  taskId: string,
): Promise<RecurrencePattern | null> {
  const rows = await dvList<RecurrencePattern>(pca, 'dagrun_pm_recurrencepatterns', {
    select: PATTERN_FIELDS,
    filter: `_dagrun_pm_parenttaskidpat_value eq ${taskId}`,
    top: 1,
  });
  return rows[0] ?? null;
}

// UI-level recurrence input. frequency+interval collapse onto the schema:
// interval 1 uses the named pattern types; interval > 1 uses CustomInterval.
export interface RecurrenceInput {
  frequency: 'daily' | 'weekly' | 'monthly';
  interval: number;                     // every N days/weeks/months, >= 1
  end: { type: 'count'; count: number } | { type: 'date'; date: string };
}

export function describeRecurrence(input: RecurrenceInput): string {
  const unit = input.frequency === 'daily' ? 'day' : input.frequency === 'weekly' ? 'week' : 'month';
  const every = input.interval === 1 ? `Every ${unit}` : `Every ${input.interval} ${unit}s`;
  const until = input.end.type === 'count'
    ? `${input.end.count} occurrence${input.end.count === 1 ? '' : 's'}`
    : `until ${input.end.date}`;
  return `${every}, ${until}`;
}

export function describePattern(p: RecurrencePattern): string {
  const type = p.dagrun_pm_patterntype;
  let every: string;
  if (type === RecurrencePatternType.Daily) every = 'Every day';
  else if (type === RecurrencePatternType.Weekly) every = 'Every week';
  else if (type === RecurrencePatternType.BiWeekly) every = 'Every 2 weeks';
  else if (type === RecurrencePatternType.Monthly) every = 'Every month';
  else if (type === RecurrencePatternType.Quarterly) every = 'Every 3 months';
  else if (type === RecurrencePatternType.CustomInterval) {
    const unit = p.dagrun_pm_intervalunit === RecurrenceIntervalUnit.Days ? 'day'
      : p.dagrun_pm_intervalunit === RecurrenceIntervalUnit.Months ? 'month' : 'week';
    every = `Every ${p.dagrun_pm_interval ?? 1} ${unit}${(p.dagrun_pm_interval ?? 1) === 1 ? '' : 's'}`;
  } else every = 'Custom schedule';
  if (p.dagrun_pm_endtype === RecurrenceEndType.AfterNOccurrences && p.dagrun_pm_endcount) {
    return `${every}, ${p.dagrun_pm_endcount} occurrences`;
  }
  if (p.dagrun_pm_endtype === RecurrenceEndType.OnDate && p.dagrun_pm_enddate) {
    return `${every}, until ${p.dagrun_pm_enddate.slice(0, 10)}`;
  }
  return every;
}

// Month addition that clamps the day-of-month (Jan 31 + 1mo = Feb 28, not
// Mar 3 — JS Date rolls over by default).
function addMonthsClamped(d: Date, months: number): Date {
  const day = d.getDate();
  const out = new Date(d.getFullYear(), d.getMonth() + months, 1);
  const lastDay = new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate();
  out.setDate(Math.min(day, lastDay));
  return out;
}

function addPeriods(base: Date, input: RecurrenceInput, n: number): Date {
  if (input.frequency === 'daily')  { const d = new Date(base); d.setDate(d.getDate() + input.interval * n); return d; }
  if (input.frequency === 'weekly') { const d = new Date(base); d.setDate(d.getDate() + 7 * input.interval * n); return d; }
  return addMonthsClamped(base, input.interval * n);
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseLocal = (s: string): Date => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Occurrence date offsets for the whole series. Occurrence n = base date
// shifted n periods; the template task itself is occurrence 0 and is not
// regenerated. Hard cap of 100 instances protects against a far-out end
// date.
export const MAX_GENERATED_INSTANCES = 100;

export function computeOccurrences(baseDueIso: string, input: RecurrenceInput): string[] {
  const base = parseLocal(baseDueIso);
  const out: string[] = [];
  if (input.end.type === 'count') {
    const n = Math.min(Math.max(1, input.end.count), MAX_GENERATED_INSTANCES);
    for (let i = 1; i <= n; i++) out.push(iso(addPeriods(base, input, i)));
    return out;
  }
  const until = parseLocal(input.end.date);
  for (let i = 1; i <= MAX_GENERATED_INSTANCES; i++) {
    const d = addPeriods(base, input, i);
    if (d > until) break;
    out.push(iso(d));
  }
  return out;
}

export interface CreateRecurrenceResult {
  patternId: string;
  instancesCreated: number;
}

/**
 * Persists the recurrence pattern for a template task, then generates the
 * instance tasks: one per occurrence, dates shifted per period, each
 * flagged dagrun_pm_isrecurringinstance and linked to the pattern via
 * dagrun_pm_parentrecurrenceid with a 1-based dagrun_pm_occurrencenumber.
 * Instance creation is sequential best-effort — a single failed occurrence
 * doesn't abort the series.
 */
export async function createRecurrence(
  pca: IPublicClientApplication,
  template: Task,
  input: RecurrenceInput,
): Promise<CreateRecurrenceResult> {
  const projectId = template._dagrun_pm_projectid_value;
  if (!projectId) throw new Error('Template task has no project');
  const baseDue = (template.dagrun_pm_duedate ?? template.dagrun_pm_startdate ?? new Date().toISOString()).slice(0, 10);
  // Start/due keep their distance across occurrences when both are set.
  const spanDays = template.dagrun_pm_startdate && template.dagrun_pm_duedate
    ? Math.max(0, Math.round((parseLocal(template.dagrun_pm_duedate).getTime() - parseLocal(template.dagrun_pm_startdate).getTime()) / 86400000))
    : null;

  const patternBody: Record<string, unknown> = {
    dagrun_pm_name: `${template.dagrun_pm_name} — ${describeRecurrence(input)}`.slice(0, 200),
    'dagrun_pm_parenttaskidpat@odata.bind': `/dagrun_pm_tasks(${template.dagrun_pm_taskid})`,
  };
  if (input.interval === 1) {
    patternBody.dagrun_pm_patterntype =
      input.frequency === 'daily' ? RecurrencePatternType.Daily
      : input.frequency === 'weekly' ? RecurrencePatternType.Weekly
      : RecurrencePatternType.Monthly;
  } else {
    patternBody.dagrun_pm_patterntype = RecurrencePatternType.CustomInterval;
    patternBody.dagrun_pm_interval = input.interval;
    patternBody.dagrun_pm_intervalunit =
      input.frequency === 'daily' ? RecurrenceIntervalUnit.Days
      : input.frequency === 'weekly' ? RecurrenceIntervalUnit.Weeks
      : RecurrenceIntervalUnit.Months;
  }
  if (input.end.type === 'count') {
    patternBody.dagrun_pm_endtype = RecurrenceEndType.AfterNOccurrences;
    patternBody.dagrun_pm_endcount = input.end.count;
  } else {
    patternBody.dagrun_pm_endtype = RecurrenceEndType.OnDate;
    patternBody.dagrun_pm_enddate = input.end.date;
  }

  const patternId = await dvCreate(pca, 'dagrun_pm_recurrencepatterns', patternBody);

  const occurrences = computeOccurrences(baseDue, input);
  let created = 0;
  for (let i = 0; i < occurrences.length; i++) {
    const due = occurrences[i];
    const body: Record<string, unknown> = {
      dagrun_pm_name: template.dagrun_pm_name,
      dagrun_pm_status: 100000000,
      dagrun_pm_percentcomplete: 0,
      dagrun_pm_isrecurringinstance: true,
      dagrun_pm_occurrencenumber: i + 1,
      dagrun_pm_duedate: due,
      'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
      'dagrun_pm_parentrecurrenceid@odata.bind': `/dagrun_pm_recurrencepatterns(${patternId})`,
    };
    if (spanDays != null) {
      const start = parseLocal(due);
      start.setDate(start.getDate() - spanDays);
      body.dagrun_pm_startdate = iso(start);
    }
    if (template._dagrun_pm_wipcollectionid_value) {
      body['dagrun_pm_wipcollectionid@odata.bind'] = `/dagrun_pm_collections(${template._dagrun_pm_wipcollectionid_value})`;
    }
    if (template.dagrun_pm_assigneeid)   body.dagrun_pm_assigneeid = template.dagrun_pm_assigneeid;
    if (template.dagrun_pm_assigneename) body.dagrun_pm_assigneename = template.dagrun_pm_assigneename;
    if (template.dagrun_pm_priority != null) body.dagrun_pm_priority = template.dagrun_pm_priority;
    if (template.dagrun_pm_estimatedhours != null) body.dagrun_pm_estimatedhours = template.dagrun_pm_estimatedhours;
    if (template.dagrun_pm_description) body.dagrun_pm_description = template.dagrun_pm_description;
    try {
      await dvCreate(pca, 'dagrun_pm_tasks', body);
      created++;
    } catch (e) {
      console.warn(`createRecurrence: occurrence ${i + 1} failed`, e);
    }
  }
  return { patternId, instancesCreated: created };
}

/**
 * Removes the recurrence pattern. Generated instances are intentionally
 * left in place — they are real tasks; deleting the rule shouldn't destroy
 * scheduled work.
 */
export async function deleteRecurrencePattern(
  pca: IPublicClientApplication,
  patternId: string,
): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_recurrencepatterns', patternId);
}
